import React from "react";
import { AppBar, Box, Divider, Toolbar, Typography } from "@mui/material";
import LogoIcon from "../../VectorIcons/LogoIcon";
import UserMenu from "../UserMenu/UserMenu";
import NavigationMenu from "../NavigationMenu/NavigationMenu";
import { focusRingOnDarkSx } from "../../../styles/focusRing";

const HEADER_GRADIENT =
  "var(--gradient-header, linear-gradient(90deg, #0079FD -37.91%, #2D51FB 20.11%, #5A29FA 78.13%, #8800F8 137.91%))";

const createAppBarSx = (sx) => ({
  background: HEADER_GRADIENT,
  minHeight: 56,
  justifyContent: "center",
  borderBottom: "1px solid",
  borderImage: `${HEADER_GRADIENT} 1`,
  boxShadow: "none",
  ...sx,
});

const createToolbarSx = () => ({
  minHeight: "56px !important",
  px: { xs: 2, sm: 2.5 },
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 2,
});

const createMainContentBoxSx = () => ({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  flex: 1,
  gap: 1.5,
});

const createLogoButtonSx = (hasClickHandler) => ({
  p: 0,
  m: 0,
  border: 0,
  background: "transparent",
  display: "flex",
  alignItems: "center",
  cursor: hasClickHandler ? "pointer" : "default",
  flexShrink: 0,
  "&:focus-visible": focusRingOnDarkSx,
});

const createDividerSx = () => ({
  height: 20,
  alignSelf: "center",
  borderColor: "#FFFFFF",
  borderRightWidth: 2,
});

const createTitleGroupBoxSx = () => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  flex: 1,
});

const createTitleSx = () => ({
  color: "#FFFFFF",
  fontSize: 20,
  fontWeight: 400,
  lineHeight: 1.1,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const createSubtitleSx = () => ({
  color: "rgba(255,255,255,0.85)",
  fontSize: 12,
  lineHeight: 1.3,
  mt: 0.25,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const createLeftContentBoxSx = () => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  ml: 1.5,
  flexWrap: "wrap",
});

const createRightContentBoxSx = () => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexShrink: 0,
  flexWrap: "wrap",
  justifyContent: "flex-end",
});

export const Header = ({
  title = "Script",
  // pages that render their own <h1> pass "p" so the document keeps a single h1
  titleComponent = "h1",
  subtitle,
  logo,
  onLogoClick,
  logoAriaLabel,
  leftContent,
  rightContent,
  actions,
  sx,
}) => {
  const appBarSx = createAppBarSx(sx);
  const toolbarSx = createToolbarSx();
  const mainContentBoxSx = createMainContentBoxSx();
  const logoButtonSx = createLogoButtonSx(!!onLogoClick);
  const dividerSx = createDividerSx();
  const titleGroupBoxSx = createTitleGroupBoxSx();
  const titleSx = createTitleSx();
  const subtitleSx = createSubtitleSx();
  const leftContentBoxSx = createLeftContentBoxSx();
  const rightContentBoxSx = createRightContentBoxSx();

  return (
    <AppBar
      position="static"
      elevation={0}
      component="header"
      sx={{
        ...appBarSx,
        ...sx,
      }}
    >
      <Toolbar disableGutters sx={toolbarSx}>
        <Box sx={mainContentBoxSx}>
          <NavigationMenu />

          <Box
            component={onLogoClick ? "button" : "div"}
            onClick={onLogoClick}
            aria-label={
              onLogoClick
                ? (logoAriaLabel ?? `${title} - página inicial`)
                : undefined
            }
            sx={logoButtonSx}
          >
            {logo ?? <LogoIcon />}
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            aria-hidden="true"
            sx={dividerSx}
          />

          <Box sx={titleGroupBoxSx}>
            <Typography component={titleComponent} sx={titleSx}>
              {title}
            </Typography>

            {subtitle && (
              <Typography component="p" sx={subtitleSx}>
                {subtitle}
              </Typography>
            )}
          </Box>

          {leftContent && <Box sx={leftContentBoxSx}>{leftContent}</Box>}
        </Box>

        <Box sx={rightContentBoxSx}>
          {rightContent}
          {actions}

          <UserMenu />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
